/**
 * Configurable parent-side widget manager (singleton pattern)
 * Creates iframe manager instances parameterized by widget name.
 */
import { generateUniqueId, getDefaultStyles, injectStyles, debugLog } from './utils';
import { sendHandshake } from './handshake';
import { createMessageHandler } from './message-handler';
import { createHashScrollController } from './hash-scroll';
const DEFAULTS = {
    maxRetries: 5,
    retryDelay: 100,
    debugMode: false,
    injectStyles: true,
    enableHashScrollFix: true,
    hashScrollOffset: 0,
    hashScrollWindowMs: 10000,
    onHeightUpdate: () => { },
    onScrollRequest: () => { },
    onHandshakeComplete: () => { },
    onHandshakeFailed: () => { },
};
export function createWidgetManager(options) {
    const { logPrefix, idPrefix, iframeClass, wrapClass, styleId, scriptSrcMatch } = options;
    let instance = null;
    class WidgetManagerInstance {
        #config;
        #widgets = new Map();
        #messageHandlerBound = null;
        #hashScroll = null;
        #initialized = false;
        constructor(config = {}) {
            this.#config = {
                iframeSelector: `.${iframeClass}`,
                ...DEFAULTS,
                ...config,
            };
        }
        setup() {
            if (this.#initialized)
                return;
            debugLog(this.#config.debugMode, logPrefix, 'Initializing');
            if (this.#config.injectStyles) {
                injectStyles(getDefaultStyles(iframeClass, wrapClass), styleId);
            }
            // Arm before iframes load so a user scroll during loading aborts the re-pin.
            this.#hashScroll = createHashScrollController(this.#config, logPrefix);
            this.#initializeIframes();
            this.#messageHandlerBound = createMessageHandler(this.#config, logPrefix, this.#hashScroll);
            window.addEventListener('message', this.#messageHandlerBound);
            this.#initialized = true;
            debugLog(this.#config.debugMode, logPrefix, `Initialized ${this.#widgets.size} widget(s)`);
        }
        #initializeIframes() {
            const iframes = document.querySelectorAll(this.#config.iframeSelector);
            iframes.forEach((iframe) => {
                if (!iframe.id)
                    iframe.id = generateUniqueId(idPrefix);
                const widgetId = iframe.id;
                if (this.#widgets.has(widgetId))
                    return;
                const wi = {
                    widgetId,
                    iframe,
                    currentHeight: null,
                    handshakeComplete: false,
                };
                this.#widgets.set(widgetId, wi);
                iframe.addEventListener('load', () => {
                    debugLog(this.#config.debugMode, logPrefix, 'Load event for', widgetId);
                    sendHandshake(iframe, this.#config, logPrefix, 0);
                });
                setTimeout(() => sendHandshake(iframe, this.#config, logPrefix, 0), 500);
            });
        }
        addIframe(iframe) {
            if (!iframe.id)
                iframe.id = generateUniqueId(idPrefix);
            const widgetId = iframe.id;
            if (this.#widgets.has(widgetId))
                return;
            this.#widgets.set(widgetId, { widgetId, iframe, currentHeight: null, handshakeComplete: false });
            if (iframe.contentWindow)
                sendHandshake(iframe, this.#config, logPrefix, 0);
        }
        destroy() {
            if (this.#messageHandlerBound) {
                window.removeEventListener('message', this.#messageHandlerBound);
                this.#messageHandlerBound = null;
            }
            if (this.#hashScroll) {
                this.#hashScroll.destroy();
                this.#hashScroll = null;
            }
            this.#widgets.clear();
            this.#initialized = false;
            instance = null;
            debugLog(this.#config.debugMode, logPrefix, 'Widget destroyed');
        }
    }
    return {
        init(config = {}) {
            if (!instance) {
                instance = new WidgetManagerInstance(config);
                instance.setup();
            }
            else {
                console.warn(`[${logPrefix}] Already initialized.`);
            }
            return instance;
        },
        getInstance() {
            return instance;
        },
        autoInit() {
            const doInit = () => {
                const scriptTag = document.querySelector(`script[src*="${scriptSrcMatch}"][data-auto-init]`);
                if (scriptTag) {
                    const debugMode = scriptTag.getAttribute('data-debug') === 'true';
                    const config = { debugMode };
                    // data-hash-scroll-offset="80" — np. wysokość sticky nav hosta
                    const offsetAttr = scriptTag.getAttribute('data-hash-scroll-offset');
                    if (offsetAttr !== null) {
                        const offset = parseInt(offsetAttr, 10);
                        if (!Number.isNaN(offset))
                            config.hashScrollOffset = offset;
                    }
                    // data-hash-scroll-fix="false" — wyłącza re-scroll do hasha
                    if (scriptTag.getAttribute('data-hash-scroll-fix') === 'false') {
                        config.enableHashScrollFix = false;
                    }
                    this.init(config);
                }
            };
            if (document.readyState === 'loading') {
                document.addEventListener('DOMContentLoaded', doInit);
            }
            else {
                doInit();
            }
        },
    };
}
